import { useEffect, useMemo, useState } from 'react'
import { Box, ListItemText, MenuItem } from '@mui/material'
import SelectedVenueIcon from '@mui/icons-material/Check'

import useAppStore from '$appStore'
import { logger } from '$utils'
import { ILoadedVenue } from '$models'

import VenueTriggerButton from './VenueTriggerButton'
import VenuePanel from './VenuePanel'
import { VENUE_SEARCH_THRESHOLD, filterVenuesByQuery } from './venueFilter'
import {
  APP_CONTEXT_CHANGE_EVENT,
  REACT_VENUE_CHANGE_EVENT,
  SWITCHING_FALLBACK_TIMEOUT_MS,
  writeSharedVenueStorage,
} from './venueSwitcherBridge'

type SingleVenueSelectorProps = {
  readonly venues: readonly ILoadedVenue[]
  readonly anchorElement: HTMLElement | null
  readonly onOpen: (event: React.MouseEvent<HTMLElement>) => void
  readonly onClose: () => void
}

/**
 * Single-venue mode of the venue switcher: pick one active venue from the
 * panel. The active venue is displayed in the topbar; long venue names
 * ellipsize with a native title tooltip. Note that we don't try and show
 * venue settings etc in here, those are managed in the App Menu which sits
 * beside this in the topbar and holds more contextual info.
 *
 * On selection React writes the shared storage keys, optimistically updates
 * the store, and hands off to AngularJS via `@@loaded/venueChange` to make its
 * changes which will result in the app context being updated.
 */
const SingleVenueSelector = ({
  venues,
  anchorElement,
  onOpen,
  onClose,
}: SingleVenueSelectorProps) => {
  const activeCompany = useAppStore((state) => state.activeCompany)

  const [isSwitching, setIsSwitching] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Clear the transient "Switching ..." state once AngularJS finishes the
  // company cascade. The bridge announces this with an `appContextChange`
  // event of type 'company'; we can't rely on a remount instead because the
  // topbar deliberately renders outside the remount-keyed content tree (see
  // TopbarPortal's mounting in components/Routes.tsx).
  useEffect(() => {
    if (!isSwitching) {
      return
    }

    const handleContextChange = (event: Event) => {
      if ((event as CustomEvent).detail?.type === 'company') {
        setIsSwitching(false)
      }
    }

    window.addEventListener(APP_CONTEXT_CHANGE_EVENT, handleContextChange)

    // Fallback: if AngularJS never broadcasts the company change (the event is
    // dropped, or the cascade errors) clear the transient state anyway so the
    // trigger can't get stuck disabled showing "Switching ..." indefinitely.
    const fallbackTimeout = window.setTimeout(
      () => setIsSwitching(false),
      SWITCHING_FALLBACK_TIMEOUT_MS
    )

    return () => {
      window.removeEventListener(APP_CONTEXT_CHANGE_EVENT, handleContextChange)
      window.clearTimeout(fallbackTimeout)
    }
  }, [isSwitching])

  const searchEnabled = venues.length > VENUE_SEARCH_THRESHOLD

  const filteredVenues = useMemo(
    () => filterVenuesByQuery(venues, searchQuery),
    [venues, searchQuery]
  )

  const activeVenueName = activeCompany?.name ?? ''

  // The active company carries only its full name; its short name lives on the
  // matching venue in the list (getAllVenues sets shortName). Used as the
  // compact mobile trigger label, falling back to the full name.
  const activeVenueShortName = useMemo(() => {
    const activeVenue = venues.find((venue) => venue.id === activeCompany?.id)
    return activeVenue?.shortName ?? activeVenueName
  }, [venues, activeCompany?.id, activeVenueName])

  // Reset the filter on close so reopening always shows the full list.
  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  const handleSelectVenue = (venue: ILoadedVenue) => {
    handleClose()

    // Selecting the already-active venue is a no-op.
    if (venue.id === activeCompany?.id) {
      return
    }

    if (!venue.id) {
      logger.warn('Venue switch ignored - selected venue has no id')
      return
    }

    logger.info('Switching active venue to %s', venue.id)

    // 1. Persist the shared keys AngularJS reads on bootstrap.
    writeSharedVenueStorage(venue.id)

    // 2. Show the transient "Switching ..." state, cleared by the
    //    appContextChange listener above once the company cascade completes.
    setIsSwitching(true)

    // 3. Hand off to AngularJS, eventually the react app will hear about the
    //    change via an app context update
    window.dispatchEvent(
      new CustomEvent(REACT_VENUE_CHANGE_EVENT, {
        detail: { venueId: venue.id },
      })
    )
  }

  return (
    <Box>
      <VenueTriggerButton
        label={isSwitching ? 'Switching ...' : activeVenueName}
        shortLabel={isSwitching ? 'Switching ...' : activeVenueShortName}
        disabled={isSwitching}
        isOpen={Boolean(anchorElement)}
        onClick={onOpen}
      />

      <VenuePanel
        anchorElement={anchorElement}
        onClose={handleClose}
        searchEnabled={searchEnabled}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchPlaceholder={`Search ${venues.length} venues`}
        emptyMessage={
          filteredVenues.length === 0
            ? `No venues match '${searchQuery.trim()}'`
            : null
        }
      >
        {filteredVenues.map((venue) => {
          const isActive = venue.id === activeCompany?.id

          return (
            <MenuItem
              key={venue.id}
              selected={isActive}
              onClick={() => handleSelectVenue(venue)}
              sx={{ paddingTop: 1, paddingBottom: 1 }}
            >
              <ListItemText
                primary={venue.shortName}
                primaryTypographyProps={{
                  noWrap: true,
                  // Native tooltip so an ellipsized name is still readable.
                  title: venue.shortName,
                  fontWeight: isActive ? 600 : 400,
                }}
              />
              {isActive && (
                <SelectedVenueIcon
                  fontSize="small"
                  color="primary"
                  sx={{ marginLeft: 1, flexShrink: 0 }}
                />
              )}
            </MenuItem>
          )
        })}
      </VenuePanel>
    </Box>
  )
}

export default SingleVenueSelector
